import { createFileRoute, notFound } from '@tanstack/react-router';
import type { ComponentType, ReactNode } from 'react';

import { getGetEquityDetailQueryOptions } from '#/api/generated/equity/equity';
import { getGetETFDetailQueryOptions } from '#/api/generated/etf/etf';
import { getGetMutualFundDetailQueryOptions } from '#/api/generated/mutual-fund/mutual-fund';
import { DetailSkeleton } from '#/components/detail/DetailSkeleton';
import { EquityDetail } from '#/components/detail/EquityDetail';
import { ETFDetail } from '#/components/detail/ETFDetail';
import { MutualFundDetail } from '#/components/detail/MutualFundDetail';
import type { DetailViewProps } from '#/components/detail/types';
import { CategoryNavigation } from '#/components/layout/CategoryNavigation';
import { DevSpacer } from '#/components/layout/DevSpacer';
import { FullPageNotFound } from '#/components/ui/FullPageNotFound';
import { NotFound } from '#/components/ui/NotFound';
import {
  equityDetailContentOptions,
  etfDetailContentOptions,
  mutualFundDetailContentOptions,
} from '#/content/queries';
import { IMPLEMENTED_TYPES } from '#/lib/api-client';
import {
  getInteriorCategoryActiveHref,
  INTERIOR_NAV_CTA,
  INTERIOR_NAV_PRODUCT_TYPES,
  INTERIOR_NAV_SECTORS,
  isProductTypeCategory,
} from '#/lib/category-navigation';
import { queryClient } from '#/lib/query-client';
import type { ProductTypeSlug } from '#/types';

// Per-type detail views
const DETAIL_VIEWS: Partial<
  Record<ProductTypeSlug, ComponentType<DetailViewProps>>
> = {
  equities: EquityDetail,
  'mutual-funds': MutualFundDetail,
  etfs: ETFDetail,
};

// Per-type content-document query options, warmed by the loader
const DETAIL_CONTENT_OPTIONS: Partial<
  Record<ProductTypeSlug, typeof equityDetailContentOptions>
> = {
  equities: equityDetailContentOptions,
  'mutual-funds': mutualFundDetailContentOptions,
  etfs: etfDetailContentOptions,
};

// Per-type detail prefetches, run by the loader. These use the same generated
// option factories the views' `useGet*Detail` hooks build on, so the query keys
// match and a mounted view adopts the request the loader already started.
const PREFETCH_DETAIL: Partial<
  Record<ProductTypeSlug, (symbol: string) => Promise<void>>
> = {
  equities: (symbol) =>
    queryClient.prefetchQuery(getGetEquityDetailQueryOptions(symbol)),
  'mutual-funds': (symbol) =>
    queryClient.prefetchQuery(getGetMutualFundDetailQueryOptions(symbol)),
  etfs: (symbol) =>
    queryClient.prefetchQuery(getGetETFDetailQueryOptions(symbol)),
};

export const Route = createFileRoute('/$category/$id')({
  beforeLoad: ({ params }) => {
    const { category } = params;
    if (!isProductTypeCategory(category)) {
      throw notFound();
    }
    if (!IMPLEMENTED_TYPES.includes(category)) {
      throw notFound();
    }
  },
  // Warm the detail content cache before render and ensure content is available.
  loader: ({ params }) => {
    const category = params.category as ProductTypeSlug;

    // Fire-and-forget, and deliberately before the awaited content fetch: the
    // detail request used to start only once the view mounted, which put it
    // behind content on the wire. `prefetchQuery` never rejects.
    void PREFETCH_DETAIL[category]?.(params.id);

    const contentOptions = DETAIL_CONTENT_OPTIONS[category];
    return contentOptions
      ? queryClient.ensureQueryData(contentOptions()).catch(() => null)
      : null;
  },
  notFoundComponent: () => <FullPageNotFound message="Security not found." />,
  // Show the skeleton for the whole wait, including the loader's content
  // fetch — without these the router renders nothing for the first second.
  // Neither delay buys anything here: the pending state and the view's own
  // loading state are the same skeleton, so there is nothing to flicker
  // between, and a minimum would only hold data back on a warm cache.
  pendingMs: 0,
  pendingMinMs: 0,
  pendingComponent: SecurityDetailPending,
  component: SecurityDetailPage,
});

/**
 * Chrome that stands outside the detail data — rendered in the pending state
 * too, so the category nav paints immediately rather than arriving with the
 * page.
 */
function DetailPageShell({ children }: { children: ReactNode }) {
  const { category } = Route.useParams();

  return (
    <>
      <DevSpacer />
      <CategoryNavigation
        productTypes={INTERIOR_NAV_PRODUCT_TYPES}
        sectors={INTERIOR_NAV_SECTORS}
        cta={INTERIOR_NAV_CTA}
        activeHref={getInteriorCategoryActiveHref(category)}
        enableSearchPreview
      />
      {children}
    </>
  );
}

function SecurityDetailPending() {
  const { category, id } = Route.useParams();

  return (
    <DetailPageShell>
      <DetailSkeleton category={category as ProductTypeSlug} symbol={id} />
    </DetailPageShell>
  );
}

function SecurityDetailPage() {
  const { category, id } = Route.useParams();
  const DetailView = DETAIL_VIEWS[category as ProductTypeSlug];

  return (
    <DetailPageShell>
      {DetailView ? (
        <DetailView category={category as ProductTypeSlug} id={id} />
      ) : (
        <NotFound message="Security not found." />
      )}
    </DetailPageShell>
  );
}
